import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Search, Filter, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ProductCard from '@/components/ProductCard';

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const category = searchParams.get('category') || '';
  const sport = searchParams.get('sport') || '';
  const featured = searchParams.get('featured') || '';

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (sport) params.append('sport', sport);
        if (featured) params.append('featured', featured);
        if (searchQuery) params.append('search', searchQuery);

        const response = await axios.get(`${API}/products?${params.toString()}`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category, sport, featured, searchQuery]);

  const handleCategoryChange = (value) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set('category', value);
    } else {
      params.delete('category');
    }
    setSearchParams(params);
  };

  const handleSportChange = (value) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set('sport', value);
    } else {
      params.delete('sport');
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchQuery('');
  };

  const hasActiveFilters = category || sport || featured || searchQuery;

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'mystery_box', label: 'Mystery Boxes' },
    { value: 'jersey', label: 'Jerseys' },
    { value: 'helmet', label: 'Helmets' },
    { value: 'ball', label: 'Balls' },
    { value: 'photo', label: 'Photos' },
    { value: 'funko', label: 'Funko Pops' },
  ];

  const sports = [
    { value: 'all', label: 'All Sports' },
    { value: 'football', label: 'Football' },
    { value: 'basketball', label: 'Basketball' },
    { value: 'baseball', label: 'Baseball' },
    { value: 'soccer', label: 'Soccer' },
    { value: 'multi', label: 'Multi-Sport' },
  ];

  const getPageTitle = () => {
    if (category) {
      const cat = categories.find(c => c.value === category);
      return cat?.label || 'Products';
    }
    if (sport) {
      const s = sports.find(sp => sp.value === sport);
      return `${s?.label || ''} Memorabilia`;
    }
    if (featured) return 'Featured Items';
    return 'All Products';
  };

  return (
    <div className="pt-20 min-h-screen" data-testid="products-page">
      {/* Header */}
      <section className="py-12 border-b border-[#27272A]">
        <div className="container-main">
          <h1 className="heading-display text-4xl md:text-5xl text-white mb-4" data-testid="products-title">
            {getPageTitle()}
          </h1>
          <p className="text-white/60">
            {products.length} {products.length === 1 ? 'item' : 'items'} available
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b border-[#27272A] sticky top-20 z-40 bg-[#050505]/95 backdrop-blur-xl">
        <div className="container-main">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Desktop Filters */}
            <div className="hidden md:flex items-center gap-4">
              <Select value={category || 'all'} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-[180px] input-field" data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-[#27272A]">
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-white/10">
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={sport || 'all'} onValueChange={handleSportChange}>
                <SelectTrigger className="w-[180px] input-field" data-testid="sport-filter">
                  <SelectValue placeholder="Sport" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-[#27272A]">
                  {sports.map((s) => (
                    <SelectItem key={s.value} value={s.value} className="text-white hover:bg-white/10">
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  onClick={clearFilters}
                  className="text-white/60 hover:text-white"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>

            {/* Mobile Filter Toggle */}
            <Button
              variant="ghost"
              className="md:hidden text-white border border-[#27272A]"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="mobile-filter-toggle"
            >
              <Filter className="w-5 h-5 mr-2" />
              Filters
            </Button>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="md:hidden mt-4 flex flex-col gap-4" data-testid="mobile-filters">
              <Select value={category || 'all'} onValueChange={handleCategoryChange}>
                <SelectTrigger className="input-field">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-[#27272A]">
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} className="text-white">
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={sport || 'all'} onValueChange={handleSportChange}>
                <SelectTrigger className="input-field">
                  <SelectValue placeholder="Sport" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-[#27272A]">
                  {sports.map((s) => (
                    <SelectItem key={s.value} value={s.value} className="text-white">
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  onClick={clearFilters}
                  className="text-white/60 hover:text-white w-full"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-gap">
        <div className="container-main">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="aspect-square bg-[#0A0A0A] animate-pulse" />
              ))}
            </div>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
              {products.map((product) => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20" data-testid="no-products">
              <h3 className="heading-display text-2xl text-white mb-4">No Products Found</h3>
              <p className="text-white/60 mb-6">Try adjusting your filters or search query.</p>
              <Button onClick={clearFilters} className="btn-primary">
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
